import Tooltip from '@/components/atoms/Tooltip/Tooltip';
import { motion } from 'framer-motion';
import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
interface BarDialProps {
  value: number;
  onChange?: (value: number) => void;
  onChangeDone?: (value: number) => void;
  fillColor?: string;
  fillColorOpacity?: string;
  trackColor?: string;
  trackOpacity?: string;
  borderColor?: string;
  borderOpacity?: string;
  borderWidth?: string;
  width?: number | string;
  height?: number | string;
  className?: string;
  roundedness?: string;
  handleColor?: string;
  handleColorOpacity?: string;
  editable?: boolean;
  hoverContent?: string | React.ReactNode;
  showBreakpoints?: boolean;
  noOfBreakpoints?: number;
  mode?: 'leftHalf' | 'rightHalf' | 'full';
  showPercentageLabels?: boolean;
}
const BarDial: FC<BarDialProps> = ({
  value,
  onChange,
  onChangeDone,
  fillColor = 'bg-accent',
  fillColorOpacity = 'bg-opacity-100',
  trackColor = 'bg-accent',
  trackOpacity = 'bg-opacity-5',
  borderColor = 'border-transparent',
  borderOpacity = 'border-opacity-0',
  borderWidth = 'border-0',
  width: size = 'w-full',
  height = 'h-md',
  roundedness = 'rounded-2xs',
  className = '',
  handleColor = 'bg-accent',
  handleColorOpacity = 'bg-opacity-100',
  editable = false,
  hoverContent,
  showBreakpoints = false,
  noOfBreakpoints,
  mode = 'full',
  showPercentageLabels = false,
}) => {
  const barRef = useRef<HTMLDivElement>(null);
  const [isInteracting, setIsInteracting] = useState(false);
  const [internalValue, setInternalValue] = useState(value);
  useEffect(() => {
    setInternalValue(value);
  }, [value]);
  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!barRef.current || !onChange) return;
      const rect = barRef.current.getBoundingClientRect();
      const newValue = Math.round(((e.clientX - rect.left) / rect.width) * 100);
      const clampedValue = Math.min(100, Math.max(0, newValue));
      setInternalValue(clampedValue);
      onChange(clampedValue);
    },
    [onChange],
  );
  const handleMouseUp = useCallback(
    (e: MouseEvent) => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      setIsInteracting(false);
      const barElement = barRef.current;
      if (!barElement) {
        onChangeDone?.(internalValue);
        return;
      }
      const rect = barElement.getBoundingClientRect();
      const newValue = Math.round(((e.clientX - rect.left) / rect.width) * 100);
      const clampedValue = Math.min(100, Math.max(0, newValue));
      setInternalValue(clampedValue);
      onChangeDone?.(clampedValue);
    },
    [handleMouseMove, onChangeDone, internalValue],
  );
  const handleMouseDown = useCallback(
    (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
      e.preventDefault();
      if (editable) {
        setIsInteracting(true);
        handleMouseMove(e.nativeEvent);
        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
      }
    },
    [handleMouseMove, handleMouseUp, editable],
  );
  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
      if (editable) {
        setIsInteracting(true);
        handleMouseMove(e.nativeEvent);
        setTimeout(() => {
          setIsInteracting(false);
          onChangeDone?.(internalValue);
        }, 100);
      }
    },
    [handleMouseMove, editable, onChangeDone, internalValue],
  );
  const breakpoints = useMemo(() => {
    if (mode !== 'full') return null;
    const count = noOfBreakpoints || 5;
    const filled = Math.floor((value / 100) * count);
    const partialFill = (value / 100) * count - filled;
    return { count, filled, partialFill };
  }, [value, noOfBreakpoints, mode]);
  const borderClasses = useMemo(() => {
    const baseClasses = `${borderWidth} ${borderColor} ${borderOpacity}`;
    if (mode === 'leftHalf') return `${baseClasses} border-l`;
    if (mode === 'rightHalf') return `${baseClasses} border-r`;
    return baseClasses;
  }, [borderWidth, borderColor, borderOpacity, mode]);
  const percentageMarks = useMemo(() => {
    return [0, 20, 40, 60, 80, 100];
  }, []);
  const barDialContent = (
    <div
      ref={barRef}
      className={`relative flex items-center overflow-hidden ${className} ${size} ${height} ${roundedness} ${borderClasses} ${
        mode === 'leftHalf' ? 'rounded-r-none' : ''
      } ${mode === 'rightHalf' ? 'rounded-l-none' : ''} ${
        editable ? 'cursor-pointer' : 'cursor-default'
      }`}
      onMouseDown={editable ? handleMouseDown : undefined}
      onClick={editable ? handleClick : undefined}
    >
      <div
        className={`h-full w-full flex overflow-hidden relative ${
          showBreakpoints && mode === 'full'
            ? ''
            : `${trackColor} ${trackOpacity}`
        }`}
        style={{
          position: 'relative',
          gap: showBreakpoints && mode === 'full' ? '2px' : '0',
          transform: mode === 'leftHalf' ? 'scaleX(-1)' : 'none',
        }}
      >
        {showBreakpoints && mode === 'full' ? (
          Array.from({ length: breakpoints!.count }).map((_, index) => (
            <div
              key={index}
              className={`h-full flex-1 relative ${trackColor} ${trackOpacity}`}
              style={{
                borderLeft:
                  index === 0 || !showBreakpoints
                    ? 'none'
                    : `1px solid ${borderColor}`,
              }}
            >
              <div
                className={`h-full w-full ${
                  index < breakpoints!.filled
                    ? `${fillColor} ${fillColorOpacity}`
                    : index === breakpoints!.filled
                    ? `${fillColor} bg-opacity-50`
                    : ''
                }`}
                style={{
                  width:
                    index === breakpoints!.filled
                      ? `${breakpoints!.partialFill * 100}%`
                      : '100%',
                }}
              />
            </div>
          ))
        ) : (
          <motion.div
            initial={{ width: isInteracting ? `${internalValue}%` : '0%' }}
            animate={{ width: `${internalValue}%` }}
            transition={{
              duration: isInteracting ? 0 : 0.8,
              ease: 'easeInOut',
            }}
            className={`h-full ${fillColor} ${fillColorOpacity}`}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              transform: mode === 'leftHalf' ? 'scaleX(-1)' : 'none',
            }}
          />
        )}
        {}
        {showPercentageLabels && internalValue > 0 && (
          <div
            className="absolute text-xs text-black/80"
            style={{
              left: `${internalValue}%`,
              top: '50%',
              transform: 'translate(-100%, -50%)',
              marginLeft: '-4px',
            }}
          >
            {Math.round(internalValue)}%
          </div>
        )}
      </div>
    </div>
  );
  return hoverContent ? (
    <Tooltip trigger={barDialContent} alignToElementRef={barRef}>
      {hoverContent}
    </Tooltip>
  ) : (
    barDialContent
  );
};
export default BarDial;
